import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useInView, animate, AnimatePresence } from "framer-motion";
import { ArrowRight, CalendarDays, CheckCircle2, Home as HomeIcon, Building2, MapPin } from "lucide-react";
import { services, IMAGES, towns } from "@/data/services";
import { Reveal, Eyebrow, EASE } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import CTABand from "@/components/CTABand";
import Seo from "@/components/Seo";

function CountUp({ to, start }) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!start) return;
    const controls = animate(0, to, {
      duration: 1.8,
      ease: EASE,
      onUpdate: (v) => setVal(Math.round(v)),
    });
    return () => controls.stop();
  }, [start, to]);
  return <span>{val.toLocaleString()}+</span>;
}

const stats = [
  { icon: CalendarDays, value: 7, label: "Years in business", testid: "stat-years" },
  { icon: CheckCircle2, value: 3000, label: "Jobs completed across the St. Clair Corridor", testid: "stat-jobs" },
];

function StatsStrip() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <section
      ref={ref}
      data-testid="stat-strip"
      className="relative z-10 -mt-12 rounded-t-[2.5rem] bg-white md:-mt-16 md:rounded-t-[3.5rem]"
    >
      <div className="mx-auto grid max-w-[1160px] grid-cols-1 gap-10 px-5 py-14 sm:grid-cols-2 md:px-8 md:py-16">
        {stats.map((s) => (
          <div key={s.testid} className="text-center" data-testid={s.testid}>
            <s.icon size={26} strokeWidth={2} className="mx-auto text-[#043B1C]" />
            <p className="stat-number mt-4 text-5xl font-extrabold tracking-tight text-[#16241C] md:text-6xl">
              <CountUp to={s.value} start={inView} />
            </p>
            <p className="mt-3 text-xs font-bold uppercase tracking-[0.2em] text-[#5B6B60]">
              {s.label}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

const rollingWords = ["Lawn Care", "Snow Removal", "Landscaping", "Seasonal Cleanups"];

function RollingWord() {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setIndex((i) => (i + 1) % rollingWords.length), 2500);
    return () => clearInterval(id);
  }, []);
  return (
    <span className="inline-flex overflow-hidden pb-[0.08em] -mb-[0.08em] align-bottom" data-testid="hero-rolling-word">
      <AnimatePresence mode="wait">
        <motion.span
          key={index}
          className="inline-block text-[#F4BE36]"
          initial={{ y: "105%", opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: "-105%", opacity: 0 }}
          transition={{ duration: 0.35, ease: EASE }}
        >
          {rollingWords[index]}
        </motion.span>
      </AnimatePresence>
    </span>
  );
}

const heroLines = [
  <>Reliable <RollingWord /></>,
  <>Across the St. Clair Corridor</>,
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const heroScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  return (
    <div data-testid="home-page">
      <Seo
        title="Legacy Scapes Ltd, Lawn Care and Landscaping in the St. Clair Corridor"
        description="Established lawn care and landscaping serving Sarnia, Point Edward, Corunna, and the St. Clair Corridor for over 7 years. Reach out to request an estimate."
      />

      {/* Hero */}
      <section ref={heroRef} className="relative flex min-h-[92vh] items-center overflow-hidden bg-[#043B1C] pt-[76px]" data-testid="home-hero">
        <motion.img
          src={IMAGES.hero}
          alt="Riverfront property with a manicured lawn along the St. Clair Corridor"
          style={{ y: heroY, scale: heroScale }}
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#043B1C]/90 via-[#043B1C]/45 to-[#043B1C]/35" />
        <div className="relative mx-auto w-full max-w-[1160px] px-5 pb-28 pt-16 md:px-8">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.1 }}>
            <Eyebrow light>Serving the St. Clair Corridor for over 7 years</Eyebrow>
          </motion.div>
          {/* Reserved space for a future tagline — intentionally left empty */}
          <div className="h-6 md:h-8" data-testid="hero-tagline-space" aria-hidden="true" />
          <h1 className="max-w-4xl text-4xl font-extrabold leading-[1.12] tracking-tight text-white sm:text-5xl lg:text-[3.6rem]" data-testid="hero-headline">
            {heroLines.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
                <motion.span
                  className="block"
                  initial={{ y: "112%", opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.75, delay: 0.15 + i * 0.13, ease: EASE }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.55, ease: EASE }}
            className="mt-6 max-w-xl text-base leading-[1.6] text-white/80 md:text-lg"
            data-testid="hero-subheadline"
          >
            Legacy Scapes delivers professional lawn care and landscaping with dependable service
            and clear communication, helping homeowners, businesses, and property managers keep
            their properties looking their best.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7, ease: EASE }}
            className="mt-9 flex flex-wrap gap-4"
          >
            <Link
              to="/contact"
              data-testid="hero-quote-button"
              className="rounded-full bg-[#F4BE36] px-8 py-4 text-sm font-bold text-[#043B1C] transition-colors duration-300 hover:bg-white"
            >
              Request an Estimate
            </Link>
            <Link
              to="/#services"
              data-testid="hero-services-button"
              className="rounded-full border border-white/70 px-8 py-4 text-sm font-bold text-white transition-colors duration-300 hover:bg-white/10"
            >
              See Our Services
            </Link>
          </motion.div>
        </div>
      </section>

      <StatsStrip />

      <Marquee />

      {/* Services grid */}
      <section id="services" className="bg-white" data-testid="services-section">
        <div className="mx-auto max-w-[1160px] px-5 py-16 md:px-8 md:py-24">
          <Reveal>
            <Eyebrow>What We Do</Eyebrow>
            <h2 className="mt-4 max-w-2xl text-3xl font-extrabold leading-[1.15] tracking-tight text-[#16241C] md:text-[2.4rem]">
              One Standard. Every Season.
            </h2>
            <p className="mt-4 max-w-xl text-base leading-[1.6] text-[#5B6B60]">
              Four core services, done consistently, season after season.
            </p>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:gap-8">
            {services.map((s, i) => (
              <Reveal key={s.slug} delay={(i % 2) * 0.08}>
                <Link
                  to={`/services/${s.slug}`}
                  data-testid={`service-card-${s.slug}`}
                  className="group block overflow-hidden rounded-2xl border border-[#16241C]/10 bg-white transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_20px_50px_-20px_rgba(4,59,28,0.35)]"
                >
                  <div className="aspect-[16/9] overflow-hidden">
                    <img
                      src={s.image}
                      alt={s.name}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-7">
                    <h3 className="text-xl font-extrabold tracking-tight text-[#16241C]">{s.name}</h3>
                    <p className="mt-2 text-sm leading-[1.6] text-[#5B6B60]">{s.cardDesc}</p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-[#043B1C] transition-colors duration-300 group-hover:text-[#F4BE36]">
                      View service
                      <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Audience split */}
      <section className="bg-[#F4F6F3]" data-testid="audience-section">
        <div className="mx-auto max-w-[1160px] px-5 py-16 md:px-8 md:py-24">
          <Reveal>
            <Eyebrow>Two Approaches</Eyebrow>
            <h2 className="mt-4 max-w-2xl text-3xl font-extrabold leading-[1.15] tracking-tight text-[#16241C] md:text-[2.4rem]">
              One Standard. Every Property.
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-6 lg:grid-cols-2 lg:gap-8">
            <Reveal>
              <Link
                to="/residential"
                data-testid="audience-residential-card"
                className="group block h-full rounded-2xl border border-[#16241C]/10 bg-white p-9 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_20px_50px_-20px_rgba(4,59,28,0.35)]"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#043B1C]">
                  <HomeIcon size={22} className="text-[#F4BE36]" strokeWidth={2.2} />
                </span>
                <h3 className="mt-6 text-2xl font-extrabold tracking-tight text-[#16241C]">For Homeowners</h3>
                <p className="mt-3 text-base leading-[1.6] text-[#5B6B60]">
                  Sign up, choose a plan, and let your crew handle it. Predictable visits,
                  predictable billing, one less thing on your list.
                </p>
                <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-bold text-[#043B1C] transition-colors duration-300 group-hover:text-[#F4BE36]">
                  Explore Residential Service
                  <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </Link>
            </Reveal>
            <Reveal delay={0.1}>
              <Link
                to="/commercial"
                data-testid="audience-commercial-card"
                className="group block h-full rounded-2xl bg-[#043B1C] p-9 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_20px_50px_-20px_rgba(4,59,28,0.5)]"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white/10">
                  <Building2 size={22} className="text-[#F4BE36]" strokeWidth={2.2} />
                </span>
                <h3 className="mt-6 text-2xl font-extrabold tracking-tight text-white">For Commercial and Property Management</h3>
                <p className="mt-3 text-base leading-[1.6] text-white/70">
                  We build contracts around your property, not the other way around. Consistent
                  communication and a crew that treats your site like it matters, because it does.
                </p>
                <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-bold text-white transition-colors duration-300 group-hover:text-[#F4BE36]">
                  Explore Commercial Service
                  <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Territory */}
      <section className="bg-white" data-testid="territory-section">
        <div className="mx-auto max-w-[1160px] px-5 py-16 md:px-8 md:py-24">
          <div className="grid items-center gap-10 lg:grid-cols-2">
            <Reveal>
              <Eyebrow>Where We Work</Eyebrow>
              <h2 className="mt-4 text-3xl font-extrabold leading-[1.15] tracking-tight text-[#16241C] md:text-[2.4rem]">
                Proudly Rooted in the St. Clair Corridor
              </h2>
              <p className="mt-4 max-w-md text-base leading-[1.6] text-[#5B6B60]">
                Our service territory spans the St. Clair River from Point Edward to Port Lambton,
                extending east to Petrolia and including Sarnia, Corunna, and the communities in
                between.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="flex flex-wrap gap-3" data-testid="towns-list">
                {towns.map((town) => (
                  <span key={town} className="flex items-center gap-2 rounded-full border border-[#043B1C]/20 bg-[#F4F6F3] px-5 py-2.5 text-sm font-bold text-[#043B1C]">
                    <MapPin size={14} strokeWidth={2.5} className="text-[#F4BE36]" /> {town}
                  </span>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <CTABand
        heading="Ready for a Property That's Taken Care Of?"
        subtext="Reach out and we'll get back to you about your property."
      />
    </div>
  );
}
